import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { UserPlus, Search, MoreVertical, RefreshCw, Loader2, User, Folder, InfoIcon } from "lucide-react";
import PersonModal from "@/components/PersonModal";
import { Person, PersonCategory, Entry, EntryType } from "@/types";
import BottomNavigation from "@/components/BottomNavigation";
import { peopleAPI } from "@/api/people";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useTranslation } from 'react-i18next';
import { ScreenHeader, MonoValue } from "@/components/redesign";

interface SharedEntry extends Entry {
  creatorName: string;
  projectName: string;
  creatorId: string;
  projectId: string;
}

// Group structure for shared entries
interface GroupedSharedEntries {
  [creatorId: string]: {
    creatorName: string;
    projects: {
      [projectId: string]: {
        projectName: string;
        entries: SharedEntry[];
      };
    };
  };
}

const SharedEntryCard = ({ entry }: { entry: SharedEntry }) => {
  const { t } = useTranslation();

  const getDisplayEntryType = (type: EntryType): string => {
    switch (type) {
      case 'givenCheck': return t('people.entry_types.given_check');
      case 'receivedCheck': return t('people.entry_types.received_check');
      case 'laborCost': return t('people.entry_types.labor_cost');
      case 'expense': return t('people.entry_types.expense');
      case 'delivery': return t('people.entry_types.delivery');
      case 'sitePhoto': return t('people.entry_types.site_photo');
      default: return type;
    }
  };

  const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div className="flex items-start justify-between gap-3">
      <span className="w-[42%] shrink-0 text-gray-500">{label}:</span>
      <span className="w-[58%] break-words text-right font-medium text-gray-900">{value}</span>
    </div>
  );

  return (
    <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-xs">
      <div className="space-y-2 text-sm">
        <DetailRow label={t('people.entry_fields.entry_type')} value={getDisplayEntryType(entry.type)} />
        {entry.creatorName && (
          <DetailRow label={t('people.entry_fields.creator')} value={entry.creatorName} />
        )}
        {entry.projectName && (
          <DetailRow label={t('people.entry_fields.project')} value={entry.projectName} />
        )}
        <DetailRow label={t('people.entry_fields.description')} value={entry.description || '-'} />
        <DetailRow label={t('people.entry_fields.date')} value={<MonoValue value={new Date(entry.date).toLocaleDateString('tr-TR')} size="sm" />} />
        <div className="flex justify-between items-center">
          <span className="text-gray-500">{t('people.entry_fields.amount')}:</span>
          <MonoValue
            value={entry.original_amount ?? 0}
            format
            fractionDigits={2}
            suffix={` ${entry.original_currency}`}
            size="sm"
            tone="default"
          />
        </div>
        <div className="flex items-start justify-between gap-3">
          <span className="w-[42%] shrink-0 text-gray-500">{t('people.entry_fields.status')}:</span>
          <span className="w-[58%] break-words text-right font-medium text-gray-900">
            {entry.is_official ? t('people.entry_fields.official') : t('people.entry_fields.unofficial')}
            {entry.hasInvoice !== undefined && (entry.hasInvoice ? ` ${t('people.entry_fields.with_invoice')}` : ` ${t('people.entry_fields.without_invoice')}`)}
          </span>
        </div>
      </div>
    </div>
  );
};

const EntryCardPlaceholder = ({ entry }: { entry: Entry }) => {
  const { t } = useTranslation();

  return (
    <div className="p-3 border border-gray-200 rounded-xl bg-white mb-2">
      <p className="font-medium text-gray-900">{entry.description || t('people.entry_fields.no_description')}</p>
      <p className="text-sm text-gray-500">{new Date(entry.date).toLocaleDateString()}</p>
      <MonoValue
        value={entry.original_amount ?? 0}
        format
        fractionDigits={2}
        suffix={` ${entry.original_currency}`}
        size="md"
        className="mt-1 block"
      />
      <p className="text-xs text-gray-500 mt-1">
        {entry.is_official ? t('people.entry_fields.official') : t('people.entry_fields.unofficial')} {entry.hasInvoice ? t('people.entry_fields.with_invoice') : t('people.entry_fields.without_invoice')}
      </p>
    </div>
  );
};

const PeoplePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [filter, setFilter] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedPersonForEdit, setSelectedPersonForEdit] = useState<Person | null>(null);
  const [peopleData, setPeopleData] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const [sharedEntries, setSharedEntries] = useState<SharedEntry[]>([]);
  const [groupedSharedEntries, setGroupedSharedEntries] = useState<GroupedSharedEntries>({});
  const [isUpdatingShared, setIsUpdatingShared] = useState(false);
  const [loadingSharedEntries, setLoadingSharedEntries] = useState(true);
  const [activeTab, setActiveTab] = useState("myRecords");
  const groupedLetterRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    const fetchPeople = async () => {
      setLoading(true);
      try {
        const data = await peopleAPI.getPeople();
        // Cast category from string to PersonCategory
        const typedData = data.map(p => ({ ...p, category: p.category as PersonCategory }));
        setPeopleData(typedData);
      } catch (error) {

      } finally {
        setLoading(false);
      }
    };

    fetchPeople();
  }, []);

  // Use callback to prevent infinite render loop
  const processSharedRecordsUpdate = useCallback(async (userId: string) => {
    setIsUpdatingShared(true);

    try {
      const fetchedEntries: SharedEntry[] = await peopleAPI.linkAndFetchSharedEntries(userId);

      // Process entries to ensure necessary fields are present
      const processedEntries = fetchedEntries.map(entry => {
        // Force all expected properties to be properly set
        return {
          ...entry,
          creatorId: entry.creatorId || (entry as any).user_id || 'unknown',
          projectId: entry.projectId || (entry as any).project_id || 'unknown',
          creatorName: entry.creatorName ||
                      (entry as any).creator_name ||
                      (entry as any).users?.name ||
                      t('people.unknown_creator'),
          projectName: entry.projectName ||
                      (entry as any).project_name ||
                      (entry as any).project?.name ||
                      t('people.unknown_project')
        };
      });

      // Use functional update to ensure we work with the latest state
      setSharedEntries(prevEntries => {
        // Sync logic: Keep existing shared entries and add new ones, remove ones not in the fetched set
        if (processedEntries.length > 0) {
          const currentEntries = [...prevEntries]; // Use the previous state passed to the function
          const currentEntryIds = new Set(currentEntries.map(entry => entry.id));
          const fetchedEntryIds = new Set(processedEntries.map(entry => entry.id));

          // Find entries to add (new entries not in current set)
          const entriesToAdd = processedEntries.filter(entry => !currentEntryIds.has(entry.id));

          // Find entries to remove (entries in current set but not in fetched set)
          const entriesToRemove = currentEntries.filter(entry => !fetchedEntryIds.has(entry.id));

          // Apply changes based on the previous state
          let updatedEntries = [...currentEntries];

          // Remove entries
          if (entriesToRemove.length > 0) {
            updatedEntries = updatedEntries.filter(entry => !entriesToRemove.some(e => e.id === entry.id));
          }

          // Add new entries
          if (entriesToAdd.length > 0) {
            updatedEntries = [...updatedEntries, ...entriesToAdd];
          }

          // Only show toast if changes were actually made
          if (entriesToAdd.length > 0 || entriesToRemove.length > 0) {
            toast({
              title: t('common.success'),
              description: t('people.shared_records_updated', { added: entriesToAdd.length, removed: entriesToRemove.length })
            });
          } else {
            toast({ title: t('common.info'), description: t('people.no_changes_shared') });
          }

          return updatedEntries; // Return the new state
        } else {
          // If API returns no entries, clear the local state and storage.
          if (prevEntries.length > 0) { // Only toast if there were entries to remove
            toast({ title: t('common.info'), description: t('people.no_matching_records') });
          }
          return []; // Return empty array to clear state
        }
      });

    } catch (error) {

      toast({
        title: t('people.update_failed'),
        description: error.message || t('people.shared_update_error'),
        variant: "destructive",
      });
    } finally {
      setIsUpdatingShared(false);
    }
  }, [setSharedEntries, setIsUpdatingShared, setLoadingSharedEntries, toast, peopleAPI]);

  // Use callback to prevent infinite render loop
  const handleUpdateSharedRecords = useCallback(async () => {
    if (!user) {
      toast({
        title: t('common.error'),
        description: t('people.user_not_found'),
        variant: "destructive"
      });
      return;
    }

    if (!user.id) {
      // Try to refresh user object before giving up
      try {
        const { data: sessionData } = await supabase.auth.getSession();

        if (sessionData?.session?.user?.id) {
          // Proceed with the session user ID instead
          const userId = sessionData.session.user.id;
          await processSharedRecordsUpdate(userId);
          return;
        }
      } catch (sessionError) {

      }

      toast({
        title: t('common.error'),
        description: t('people.user_id_missing'),
        variant: "destructive"
      });
      return;
    }

    await processSharedRecordsUpdate(user.id);
  }, [user, processSharedRecordsUpdate, toast]);

  // Load saved shared entries from localStorage on mount
  useEffect(() => {
    setLoadingSharedEntries(true);
    if (!user?.id) {
      setLoadingSharedEntries(false);
      return;
    }

    try {
      const savedEntries = localStorage.getItem(`santiyex-shared-entries-${user.id}`);
      if (savedEntries) {
        const parsedEntries = JSON.parse(savedEntries) as SharedEntry[];

        // Fix any entries with missing creator/project names
        const fixedEntries = parsedEntries.map(entry => {
          if (!entry.creatorName || entry.creatorName === t('people.unknown_creator')) {
            return {
              ...entry,
              creatorName: t('people.unknown_creator') // Keep default for now
            };
          }
          return entry;
        });

        setSharedEntries(fixedEntries);
        setLoadingSharedEntries(false);

        // If any entries needed fixing, force a refresh from the server
        // But do this in a non-blocking way so we don't show loading while displaying entries
        if (fixedEntries.some(e => !e.creatorName || e.creatorName === t('people.unknown_creator'))) {
          // Use setTimeout to ensure this runs after the current render cycle
          setTimeout(() => {
            handleUpdateSharedRecords();
          }, 100);
        }
      } else {
        // If no saved entries are found, trigger initial load
        // Keep loadingSharedEntries true in this case since we have no entries to show
        handleUpdateSharedRecords().finally(() => {
          setLoadingSharedEntries(false);
        });
      }
    } catch (error) {

      setLoadingSharedEntries(false);
    }
  }, [user?.id, handleUpdateSharedRecords]);

  // Process shared entries into grouped structure when they change
  useEffect(() => {


    if (sharedEntries.length === 0) {

      setGroupedSharedEntries({});
      return;
    }

    const grouped: GroupedSharedEntries = {};

    sharedEntries.forEach((entry, index) => {
      // Log each entry being processed


      // Use the fields already processed in processSharedRecordsUpdate
      const creatorId = entry.creatorId; // Should be set by processSharedRecordsUpdate
      const projectId = entry.projectId; // Should be set by processSharedRecordsUpdate
      const creatorName = entry.creatorName; // Should be set by processSharedRecordsUpdate
      const projectName = entry.projectName; // Should be set by processSharedRecordsUpdate

      // Check if essential IDs are present after processing
      if (!creatorId || creatorId === 'unknown' || !projectId || projectId === 'unknown') {

          return; // Skip if essential IDs are missing or default 'unknown'
      }

      // Initialize creator group if it doesn't exist
      if (!grouped[creatorId]) {
        grouped[creatorId] = {
          creatorName,
          projects: {}
        };
      }

      // Initialize project group if it doesn't exist
      if (!grouped[creatorId].projects[projectId]) {
        grouped[creatorId].projects[projectId] = {
          projectName,
          entries: []
        };
      }

      // Add entry to its project group with explicitly set fields
      grouped[creatorId].projects[projectId].entries.push({
        ...entry,
        creatorId,
        creatorName,
        projectId,
        projectName
      });
    });


    setGroupedSharedEntries(grouped);
  }, [sharedEntries]);

  // Save shared entries to localStorage whenever they change
  useEffect(() => {
    // Allow saving even if sharedEntries becomes empty, to clear localStorage.
    if (!user?.id) return;

    try {
      // Make sure all entries have their creator and project names
      const entriesWithNames = sharedEntries.map(entry => {
        return {
          ...entry,
          // Ensure these fields are explicitly set to avoid serialization issues
          creatorName: entry.creatorName || t('people.unknown_creator'),
          projectName: entry.projectName || t('people.unknown_project'),
          creatorId: entry.creatorId || 'unknown',
          projectId: entry.projectId || 'unknown'
        };
      });

      const serializedData = JSON.stringify(entriesWithNames);
      localStorage.setItem(`santiyex-shared-entries-${user.id}`, serializedData);
    } catch (error) {

    }
  }, [sharedEntries, user?.id]);

  const filteredPeople = peopleData.filter((person) => {
    return person.name.toLowerCase().includes(filter.toLowerCase()) ||
      (person.category && person.category.toLowerCase().includes(filter.toLowerCase())) ||
      (person.specialty && person.specialty.toLowerCase().includes(filter.toLowerCase()));
  });

  // Group people alphabetically A–Z
  const groupedPeople = filteredPeople.reduce((acc, person) => {
    const firstLetter = (person.name || '').charAt(0).toUpperCase() || '#';
    const key = /^[A-ZÇĞİÖŞÜ]/.test(firstLetter) ? firstLetter : '#';
    if (!acc[key]) acc[key] = [];
    acc[key].push(person);
    return acc;
  }, {} as Record<string, Person[]>);

  const sortedLetters = Object.keys(groupedPeople).sort((a, b) => {
    if (a === '#') return 1;
    if (b === '#') return -1;
    return a.localeCompare(b, 'tr');
  });

  const handleOpenAddModal = () => {
    setIsEditing(false);
    setSelectedPersonForEdit(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (person: Person) => {
    setSelectedPersonForEdit(person);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsEditing(false);
    setSelectedPersonForEdit(null);
  };

  const refreshPeopleData = async () => {
    setLoading(true);
    try {
      const data = await peopleAPI.getPeople();
      // Cast category from string to PersonCategory
      const typedData = data.map(p => ({ ...p, category: p.category as PersonCategory }));
      setPeopleData(typedData);
    } catch (error) {

      toast({ title: t('people.data_refresh_failed'), description: t('people.people_refresh_error'), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleAddPerson = async (data: any) => {
    try {
      // Create person data
      const personData = {
        name: data.name,
        phone: data.phone || null,
        email: data.email || '',
        category: data.category as PersonCategory,
        specialty: data.specialty || '',
        created_by: user?.id, // Add the creator's ID
      };

      if (!personData.created_by) {
        toast({ title: t('common.error'), description: t('people.user_id_missing'), variant: "destructive" });
        return false;
      }

      // Call the API to save to Supabase
      const newPerson = await peopleAPI.createPerson(personData);


      // Refresh the people data
      await refreshPeopleData();

      // Show success message
      toast({
        title: t('people.person_added'),
        description: t('people.person_added_successfully', { name: data.name }),
      });
      return true;
    } catch (error: any) {


      // Show error message with specific details if available
      toast({
        title: t('people.person_add_failed'),
        description: error.message || t('notifications.please_try_again'),
        variant: "destructive",
      });
      return false;
    }
  };

  const handleUpdatePerson = async (data: any) => {
    if (!selectedPersonForEdit) return false;

    try {
      const updateData = {
        name: data.name,
        category: data.category as PersonCategory,
        specialty: data.specialty || '',
        phone: data.phone || null,
        email: data.email || '',
        // Add other fields as needed based on PersonModal data
      };

      await peopleAPI.updatePerson(selectedPersonForEdit.id, updateData);

      await refreshPeopleData();

      toast({
        title: t('people.person_updated'),
        description: t('people.person_added_successfully', { name: data.name }),
      });
      return true;
    } catch (error: any) {

      toast({
        title: t('people.person_update_failed'),
        description: error.message || t('people.person_add_error'),
        variant: "destructive",
      });
      return false;
    }
  };

  const handleModalSubmit = async (data: any) => {
    let success = false;
    if (isEditing) {
      success = await handleUpdatePerson(data);
    } else {
      success = await handleAddPerson(data);
    }
    if (success) {
      handleCloseModal();
    }
  };

  const handlePersonClick = (personId: string) => {
    navigate(`/person/${personId}`);
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'laborer':
        return t('people.categories.laborers');
      case 'supplier':
        return t('people.categories.suppliers');
      case 'subcontractor':
        return t('people.categories.subcontractors');
      case 'employer':
        return t('people.categories.employers');
      case 'partner':
        return t('people.categories.partners');
      case 'engineering':
        return t('people.categories.engineering');
      default:
        return category || t('people.categories.uncategorized');
    }
  };

  // Get initials from a name for the avatar tile
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n.charAt(0))
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  const scrollToLetter = (letter: string) => {
    if (letter === '#') {
      const firstLetter = sortedLetters[0];
      if (firstLetter) {
        groupedLetterRefs.current[firstLetter]?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      return;
    }

    const target = groupedLetterRefs.current[letter];
    if (!target) return;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const renderPersonItem = (person: Person) => {
    const categoryLabel = getCategoryLabel(person.category);
    const subtitle = person.specialty || categoryLabel;

    return (
      <div
        key={person.id}
        className="flex items-center justify-between rounded-lg border border-brand-100 bg-brand-25 px-4 py-4 shadow-[0_2px_4px_-2px_rgba(10,13,18,0.06),0_4px_8px_-2px_rgba(10,13,18,0.10)] cursor-pointer"
        onClick={() => handlePersonClick(person.id)}
      >
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-[200px] bg-brand-100">
            <span className="text-[18px] font-medium leading-7 text-[#204876]">{getInitials(person.name)}</span>
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[16px] font-semibold leading-6 text-brand-900">{person.name}</p>
            <p className="truncate text-[16px] leading-6 text-[#3B608F]">{subtitle}</p>
          </div>
        </div>
        <div className="ml-3 flex h-12 items-center gap-2">
          {person.linked ? (
            <span className="rounded-md bg-success-100 px-2 py-1 text-[11px] font-semibold text-success-700">
              {t('people.connected')}
            </span>
          ) : null}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-7 rounded bg-brand-100 text-brand-400 hover:bg-brand-200 hover:text-brand-600"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              <DropdownMenuItem onClick={() => handleOpenEditModal(person)}>
                {t('common.edit')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    );
  };

  const renderSharedEntriesContent = () => {
    // Show loading indicator during initial load from storage/network
    if (loadingSharedEntries) {
      return <p className="text-center text-gray-500">{t('people.loading_shared_records')}</p>;
    }

    // If we have entries to display, show them even if updating in background
    const hasEntriesToDisplay = Object.keys(groupedSharedEntries).length > 0;

    // Show specific message while actively syncing ONLY if we have no entries to display
    if (isUpdatingShared && !hasEntriesToDisplay) {
      return <p className="text-center text-gray-500">{t('people.records_updating')}</p>;
    }

    // If no entries to display and not updating
    if (!hasEntriesToDisplay) {
      return (
        <div className="space-y-4 text-center">
          <p className="text-gray-500">
            {t('people.no_shared_records')}
          </p>
          <p className="text-xs text-gray-400">
            <InfoIcon className="inline h-3 w-3 mr-1" />
            {t('people.device_storage_info')}
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* Show a small indicator if updating in background */}
        {isUpdatingShared && (
          <div className="text-xs text-brand-600 bg-brand-50 border border-brand-200 p-2 rounded-lg mb-2 flex items-center">
            <Loader2 className="h-3 w-3 mr-2 animate-spin" />
            <span>{t('people.updating_background')}</span>
          </div>
        )}
        <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 p-3 rounded-xl mb-2 flex items-start">
          <InfoIcon className="h-3 w-3 mr-2 mt-0.5 flex-shrink-0" />
          <span>
            {t('people.shared_records_info')}
          </span>
        </div>
        {Object.entries(groupedSharedEntries).map(([creatorId, creator]) => (
          <Accordion key={creatorId} type="single" collapsible className="w-full">
            <AccordionItem value={`creator-${creatorId}`} className="border border-gray-200 rounded-xl overflow-hidden">
              <AccordionTrigger className="px-4 py-3 bg-gray-50 hover:no-underline hover:bg-gray-100 rounded-t-xl">
                <div className="flex items-center gap-2 text-left">
                  <User className="h-4 w-4 text-brand-600" />
                  <span className="font-medium text-gray-900">{creator.creatorName}</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="p-0 bg-white">
                <div className="space-y-1 py-2">
                  {Object.entries(creator.projects).map(([projectId, project]) => (
                    <Accordion key={projectId} type="single" collapsible className="w-full">
                      <AccordionItem value={`project-${projectId}`} className="border-0">
                        <AccordionTrigger className="px-4 py-2 hover:no-underline hover:bg-gray-50">
                          <div className="flex items-center gap-2 text-left pl-4">
                            <Folder className="h-4 w-4 text-success-600" />
                            <span className="font-medium text-gray-700">{project.projectName}</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pt-0 px-4 pb-2">
                          <div className="mt-2 space-y-2 pl-2 sm:pl-8">
                            {project.entries.map(entry => (
                              <SharedEntryCard key={entry.id} entry={entry} />
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        ))}
      </div>
    );
  };

  // Handle tab change
  const handleTabChange = (value: string) => {
    setActiveTab(value);

    // If we're switching to the shared tab and have no entries yet, load them
    // But only if we're not already loading or updating
    if (value === "sharedWithMe" && user?.id && sharedEntries.length === 0 && !loadingSharedEntries && !isUpdatingShared) {
      // Also check if we haven't already attempted to load from localStorage
      const savedEntries = localStorage.getItem(`santiyex-shared-entries-${user.id}`);
      if (!savedEntries) {
        handleUpdateSharedRecords();
      }
    }
  };

  return (
    <div className="page-container bg-white">
      <ScreenHeader
        title={t('people.people')}
        backLabel={t('navigation.home', { defaultValue: 'Anasayfa' })}
        showBack
      />

      <main className="main-content">
        <div className="px-4 pb-4">
          <Tabs
            defaultValue="myRecords"
            value={activeTab}
            onValueChange={handleTabChange}
            className="w-full"
          >
            <TabsList className="mb-4 grid w-full grid-cols-2 rounded-lg bg-[#EBF1FF] p-1">
              <TabsTrigger value="myRecords" className="rounded-md text-[14px] font-bold text-[#3B608F] data-[state=active]:bg-white data-[state=active]:text-brand-600 data-[state=active]:shadow-[0_1px_1px_rgba(10,13,18,0.05)]">
                {t('people.my_records_short', { defaultValue: 'Kayıtlarım' })}
              </TabsTrigger>
              <TabsTrigger value="sharedWithMe" className="rounded-md text-[14px] font-semibold text-[#3B608F] data-[state=active]:bg-white data-[state=active]:text-brand-600 data-[state=active]:shadow-[0_1px_1px_rgba(10,13,18,0.05)]">
                {t('people.shared_with_me_short', { defaultValue: 'Paylaşılanlar' })}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="myRecords">
              <div className="-mx-4 bg-brand-25 px-4 pb-6 pt-0">
                <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <h3 className="text-[18px] font-bold text-[#00315D]">{t('people.saved_people_title', { defaultValue: 'Kayıtlı kişiler' })}</h3>
                  <Button
                    size="sm"
                    className="h-9 w-full rounded-lg border border-brand-900 bg-brand-900 px-3 text-white shadow-[0_1px_2px_rgba(10,13,18,0.05),0_2px_2px_rgba(10,13,18,0.06),0_4px_4px_rgba(10,13,18,0.10)] hover:bg-[#0A3567] sm:w-auto"
                    onClick={handleOpenAddModal}
                  >
                    <UserPlus className="mr-1.5 h-4 w-4" />
                    {t('people.new_person_button', { defaultValue: 'Yeni Kişi' })}
                  </Button>
                </div>

                <p className="mb-4 text-[11px] leading-[16.5px] text-[#4A5980]">
                  {t('people.saved_people_description', {
                    defaultValue: 'Kaydettiğiniz tüm tedarikçi ve müşteriler, çalışanlar ve diğer tüm kontaklar burada tutulur.',
                  })}
                </p>

                <div className="flex items-center gap-2 rounded-lg border border-[#D5D7DA] bg-white px-[14px] py-[10px] shadow-[0_1px_2px_rgba(10,13,18,0.05)]">
                  <Search className="h-5 w-5 text-[#717680] flex-shrink-0" />
                  <input
                    className="w-full bg-transparent text-[16px] text-[#717680] outline-none placeholder:text-[#717680]"
                    placeholder={t('people.search_person_short', { defaultValue: 'Kişi arayın' })}
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                  />
                </div>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
                </div>
              ) : sortedLetters.length > 0 ? (
                <div className="-mx-4 bg-white px-4 pb-4 pt-1">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_23px]">
                    <div className="space-y-5">
                      {sortedLetters.map((letter) => (
                        <div
                          key={letter}
                          className="space-y-2"
                          ref={(element) => {
                            groupedLetterRefs.current[letter] = element;
                          }}
                        >
                          <h4 className="px-1 text-[18px] font-bold leading-[normal] text-[#00315D]">
                            {letter}
                          </h4>
                          <div className="space-y-2">
                            {groupedPeople[letter].map(renderPersonItem)}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="sticky top-24 hidden h-fit py-4 sm:block">
                      <div className="flex flex-col items-center gap-[5px]">
                        {['#', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')].map((letter) => (
                          <button
                            key={letter}
                            type="button"
                            onClick={() => scrollToLetter(letter)}
                            className="h-4 text-center text-[12px] leading-4 tracking-[0.4px] text-[#1A5C96]"
                          >
                            {letter}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">{t('people.no_people_added')}</p>
              )}
            </TabsContent>

            <TabsContent value="sharedWithMe">
              <div className="mb-4">
                <Button
                  onClick={handleUpdateSharedRecords}
                  disabled={isUpdatingShared}
                  className="w-full bg-brand-600 hover:bg-brand-700 text-white rounded-xl"
                >
                  {isUpdatingShared ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="mr-2 h-4 w-4" />
                  )}
                  {t('people.update_shared_records')}
                </Button>
              </div>

              {renderSharedEntriesContent()}
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <PersonModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleModalSubmit}
        isEditing={isEditing}
        initialData={isEditing ? selectedPersonForEdit : undefined}
      />

      <BottomNavigation />
    </div>
  );
};

export default PeoplePage;
